import React from "react";
import i18n from "I18n";

import { ReactHookFormMarkDown } from "components/base/CustomMarkDownArea";
import ReactHookFormTextInput from "components/reactHookFormComponents/ReactHookFormTextInput";
import { COUPON_REDEMPTION_METHOD, COUPON_SET_MEDIUM, COUPON_SET_TYPES, DISPLAY_COUPON_REDEMPTION_METHOD, DISPLAY_COUPON_SET_TYPES, LanguageConfig, TICKET_CONTENT_PAGE_ANCHOR_KEY } from "config/CustomEnums";
import BrandDropdownWithButtonAndPrompt from "components/campaign/campaignCreation/BrandDropdownWithButtonAndPrompt";
import { CustomTitleLabel } from "components/earning/CustomBaseComponments";
import StoreDropdownWithButtonAndPrompt from "components/campaign/campaignCreation/StoreDropdownWithButtonAndPrompt";
import PhotoUploaderWithSwitch from "components/campaign/campaignCreation/PhotoUploaderWithSwitch";
import ReactHookFormMarkDownArea from "components/reactHookFormComponents/ReactHookFormMarkDownArea";
import CouponDateTimeSelector from "components/coupon/couponCreation/CouponDateTimeSelector";
import ReactHookFormAdditionalDataSection from "components/reactHookFormComponents/ReactHookFormAdditionalDataSection";
import ReactHookFormTitleWithRadio from "components/reactHookFormComponents/ReactHookFormTitleWithRadio";
import TypeSelector from "components/campaignStreamline/TypeSelector";
import SystemGeneratedImg from 'assets/images/coupon/systemGenerated.svg';
import GenerateCouponSection, { CampaignStreamlineGenerateCouponSection } from "components/campaignStreamline/GenerateCouponSection";

export const TICKET_CREATION_STEP_ONE_CONFIG = {
  extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch, getValues } }) => {
    const selectedBrand = watch('selectedBrand')

    let displayNormalSectionComponents = [];
    let displayLanguageSectionComponents = [
      TICKET_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.generalNameKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.shortDescriptionKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.coverPhotoUploadKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.otherPhotoUploadKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.instruction.sectionContent.instructionSectionKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.detail.sectionContent.detailSectionKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.ticketStores.sectionContent.relatedBrandKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.validPeriod.sectionContent.validPeriodTypeKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.additionalData.sectionContent.additionalDataKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.codeSetting.sectionContent.redemptionMethodKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.codeSetting.sectionContent.ticketSourceKey,
      TICKET_CONTENT_PAGE_ANCHOR_KEY.generateTickets.sectionContent.generateTicketsKey,
    ];
    if (selectedBrand?.pk) {
      displayLanguageSectionComponents.push(
        TICKET_CONTENT_PAGE_ANCHOR_KEY.ticketStores.sectionContent.useInOneStoreKey,
      )
    }

    return {
      displayNormalSectionComponents,
      displayLanguageSectionComponents,
    };
  },
  normalSections: [],
  languageSections: [
    {
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.general.sectionKey,
      sectionTitle: 'general',
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.generalNameKey,
          component: ReactHookFormTextInput,
          componentProps: {
            valueFieldName: 'name',
            withTranslation: true,
            focusKey: 'name',
            title: 'name',
          },
        },
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.shortDescriptionKey,
          complexComponent: ({ language, setActiveSection, formContext: { setValue }, }) => (
            <ReactHookFormMarkDown
              content={{
                label: i18n.t('short_description_optional', { locale: language }),
                key: `translations.${language}.shortDescription`,
                focus: () => {
                  setActiveSection('shortDescription');
                },
              }}
            />
          ),
        },
      ]
    },
    {
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionKey,
      sectionTitle: 'key_visual',
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.coverPhotoUploadKey,
          component: PhotoUploaderWithSwitch,
          componentProps: {
            withTranslation: true,
            title: 'cover_photo',
            tips: 'cover_photo_suggest',
            tranlationFieldPrefix: 'translations',
            photoFieldName: 'coverPhoto',
            activeSectionKey: 'coverPhoto',
            switchConfig: {
              fieldName: 'applyCoverPhotoToAllLanguage',
              title: 'Apply to all language',
            },
            uploaderAspect: 900 / 600,
            minWidth: 900,
            minHeight: 600
          },
        },
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.otherPhotoUploadKey,
          component: PhotoUploaderWithSwitch,
          componentProps: {
            withTranslation: true,
            title: 'other_photos',
            tips: 'cover_photo_suggest',
            tranlationFieldPrefix: 'translations',
            photoFieldName: 'otherPhoto',
            activeSectionKey: 'otherPhotos',
            switchConfig: {
              fieldName: 'applyDetailPhotosToAllLanguage',
              title: 'Apply to all language',
            },
            uploaderAspect: 900 / 600,
            minWidth: 900,
            minHeight: 600,
            maxImageNum: 4,
          },
        },
      ],
    },
    {
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.instruction.sectionKey,
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.instruction.sectionContent.instructionSectionKey,
          component: ReactHookFormMarkDownArea,
          componentProps: {
            hasTranslation: true,
            areaTitle: "instruction",
            titleConfig: {
              label: 'instruction_section_title',
              valueFieldName:
                'translations.{{language}}.instructionSectionTitle',
              disableFocusChange: false,
              focusChangeKey: 'instructionTitle',
            },
            contentConfig: {
              label: 'instruction_section_content',
              valueFieldName:
                'translations.{{language}}.instructionSectionContent',
              disableFocusChange: false,
              focusChangeKey: 'instructionContent',
            },
          },

        },
      ],
    },
    {
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.detail.sectionKey,
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.detail.sectionContent.detailSectionKey,
          component: ReactHookFormMarkDownArea,
          componentProps: {
            hasTranslation: true,
            areaTitle: "detail",
            titleConfig: {
              label: 'detail_section_title',
              valueFieldName:
                'translations.{{language}}.detailSectionTitle',
              disableFocusChange: false,
              focusChangeKey: 'detailsTitle',
            },
            contentConfig: {
              label: 'detail_section_content',
              valueFieldName:
                'translations.{{language}}.detailSectionContent',
              disableFocusChange: false,
              focusChangeKey: 'detailsContent',
            },
          },
        },
      ],
    },
    {
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.validPeriod.sectionKey,
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.validPeriod.sectionContent.validPeriodTypeKey,
          component: CouponDateTimeSelector,
          componentProps: {
            title: "COUPON VALID PERIOD",
          },
        },
      ],
    },
    {
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionKey,
      sectionTitle: 'Ticket applicable stores',
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.ticketStores.sectionContent.relatedBrandKey,
          complexComponent: ({
            language,
            setActiveSection,
            formContext: { watch, setValue },
          }) => {
            const selectedBrand = watch('selectedBrand')
            if (language === LanguageConfig.english) {
              return (
                <BrandDropdownWithButtonAndPrompt
                  labelContainPk={false}
                  title={i18n.t('related_brand', { locale: language })}
                  defaultFilter={{ isSelectorLoad: true }}
                  valueFieldName="selectedBrand"
                  hasAddButton={true}
                  isPrompt={true}
                  promptConfig={{
                    title: 'Go to create brand?',
                    message: `You will leave the ticket creation process. After you create a new brand, you can back to this page.`,
                    button: 'Create Brand',
                    refreshButton: 'Done and refresh',
                    action: () => {
                      window.open('/brands/create', '_blank');
                    },
                  }}
                  customOnChange={(newValue) => {
                    if (newValue !== selectedBrand) {
                      setValue("ticketStore", null, { shouldDirty: true });
                      setValue("inputStoreCouponNum", null, { shouldDirty: true });
                    }
                  }}
                  onFocus={() => setActiveSection('relatedBrand')}
                />
              );
            }
            return (
              <div className="brand-select-dropdown-container">
                <CustomTitleLabel
                  title={i18n.t('related_brand', { locale: language })}
                />
                <label>{selectedBrand?.name || '-'}</label>
              </div>
            );
          },
        },
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.ticketStores.sectionContent.useInOneStoreKey,
          complexComponent: ({
            language,
            setActiveSection,
            formContext: { watch, setValue },
          }) => {
            const selectedBrand = watch('selectedBrand');
            const ticketStore = watch("ticketStore");
            if (language === LanguageConfig.english) {
              return (
                <StoreDropdownWithButtonAndPrompt
                  labelContainPk={true}
                  title={i18n.t('using_in_one_single_store', { locale: language })}
                  defaultFilter={{
                    brand: [selectedBrand?.pk],
                    isSelectorLoad: true,
                    isSimpleNode: true,
                    active: "true",
                  }}
                  valueFieldName="ticketStore"
                  hasAddButton={false}
                  isPrompt={true}
                  onFocus={() => setActiveSection('relatedBrand')}
                  customOnChange={()=>{
                    setValue("inputStoreCouponNum", null, { shouldDirty: true });
                  }}
                />
              );
            }
            return (
              <div className="brand-select-dropdown-container">
                <CustomTitleLabel
                  title={i18n.t('applicable_stores', { locale: language })}
                />
                <label>
                  {
                    ticketStore?.name || '-'
                  }
                </label>
              </div>
            );
          },
        },
      ],
    },
    {
    
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.codeSetting.sectionKey,
      sectionTitle: 'OFFLINE TICKET CODE SETTING',
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.codeSetting.sectionContent.redemptionMethodKey,
          component: ReactHookFormTitleWithRadio,
          componentProps: {
            title: "Ticket redemption method",
            valueFieldName: "offlineRedemptionMethod",
            defaultValue: COUPON_REDEMPTION_METHOD.MERCHANT_APP_SCAN_COUPON_QR_CODE,
            options: [
              COUPON_REDEMPTION_METHOD.MERCHANT_APP_SCAN_COUPON_QR_CODE,
              COUPON_REDEMPTION_METHOD.USERS_SCAN_STORE_QR_CODE,
              COUPON_REDEMPTION_METHOD.INPUT_STORE_STAFF_CODE,
            ]?.map((item) => ({
              label: DISPLAY_COUPON_REDEMPTION_METHOD?.[item] || item,
              value: item
            }))
          },
        },
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.codeSetting.sectionContent.ticketSourceKey,
          complexComponent: () => {
            return <div>
              <CustomTitleLabel title={"Ticket source"} />
              <TypeSelector
                typeConfig={[
                  {
                    id: COUPON_SET_TYPES.SYSTEM_GENERATED,
                    title: DISPLAY_COUPON_SET_TYPES?.[COUPON_SET_TYPES.SYSTEM_GENERATED],
                    image: SystemGeneratedImg,
                    description:
                      'You will need to input the quantity of tickets to be generated (per store)',
                  },
                ]}
                typeFieldName={"offlineCouponSetType"}
                defaultValue={COUPON_SET_TYPES.SYSTEM_GENERATED}
                customItemClassName='coupon-source-item'
                itemWidth={375}
              />
          </div>
          },
        },
      ],
    },
    {
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.generateTickets.sectionKey,
      sectionTitle: "Import / Generate tickets (Optional)",
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.generateTickets.sectionContent.generateTicketsKey,
          complexComponent: ({
            formContext: { watch, setValue },
          }) => {
            const inputStoreCouponField = "inputStoreCouponNum";
            const inputStoreCouponNum = watch(inputStoreCouponField)
            const selectedBrand = watch('selectedBrand');
            const ticketStore = watch("ticketStore");
            return <GenerateCouponSection
              title={"Applicable stores"}
              description={"Please input the number of tickets to be generated. You can set to max 100,000 if there is no specific limit."}
              couponSetMedium={watch("mediumType")}
              stores={ticketStore ? [ticketStore] : []}
              shouldBeUsedInStores={watch("shouldBeUsedInStores")}
              couponSetOfflineCouponSource={watch("offlineCouponSetType")}
              customGetGroupedStores={(store)=>{
                if (!ticketStore?.pk) {
                  return {}
                }
                return {
                  [selectedBrand?.translation?.en?.name || selectedBrand?.name]: store
                }
              }}
              oneStoreCouponConfig={{
                onInputStoreCouponNum: (storeID, value) => {
                  setValue(inputStoreCouponField, { ...inputStoreCouponNum, [storeID]: value }, { shouldDirty: true });
                },
                onRemoveUploadedFile: () => {},
                afterUploadFile: () => { },
                storeInputCouponNumMap: inputStoreCouponNum
              }}
          />
          }
        },
      ],
    },
    {
      sectionKey: TICKET_CONTENT_PAGE_ANCHOR_KEY.additionalData.sectionKey,
      sectionContent: [
        {
          key: TICKET_CONTENT_PAGE_ANCHOR_KEY.additionalData.sectionContent.additionalDataKey,
          component: ReactHookFormAdditionalDataSection,
          componentProps: {
            sectionKeyPrefix: TICKET_CONTENT_PAGE_ANCHOR_KEY.additionalData.sectionContent.additionalDataKey,
          }
        }
      ]
    },
  ]
}